import { useCallback } from 'react';

import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

const useEventHandlers = ({
  isDrawing,
  setIsDrawing,
  isEditingText,
  handleEraserLogic,
  handleLineMouseMove,
  handleShapeMouseMove,
  handlePenMouseUp,
  handleShapeMouseUp,
  handleLineClick,
  getEraserRefs,
  startShapeDrawing,
  createSticker,
  createNewText,
  createStickyNote,
  stageRef,
}: {
  isDrawing: boolean;
  setIsDrawing: (drawing: boolean) => void;
  isEditingText: boolean;
  handleEraserLogic: (stage: Konva.Stage, pos: { x: number; y: number }, isMouseUp: boolean) => void;
  handleLineMouseMove: (pos: { x: number; y: number }) => void;
  handleShapeMouseMove: (pos: { x: number; y: number }, stage: Konva.Stage) => void;
  handlePenMouseUp: () => void;
  handleShapeMouseUp: () => void;
  handleLineClick: (pos: { x: number; y: number }) => void;
  getEraserRefs: () => {
    erasingObjectsRef: React.MutableRefObject<Set<string>>;
    lastEraserPosRef: React.MutableRefObject<{ x: number; y: number } | null>;
  };
  startShapeDrawing: (pos: { x: number; y: number }) => void;
  createSticker: (pos: { x: number; y: number }, stageRef: React.RefObject<Konva.Stage>) => void;
  createNewText: (pos: { x: number; y: number }, stageRef: React.RefObject<Konva.Stage>) => void;
  createStickyNote: (e: Konva.KonvaEventObject<MouseEvent>, stageRef: React.RefObject<Konva.Stage>) => void;
  stageRef: React.RefObject<Konva.Stage>;
}) => {
  const {
    activeTool,
    activeShape,
    activeSticker,
    currentShape,
    lineStartPoint,
    shapeStartPoint,
    currentLinePoints,
    setCurrentLinePoints,
    setShapeStartPoint,
  } = useMagicPenStore(
    useShallow((state) => ({
      activeTool: state.activeTool,
      activeShape: state.activeShape,
      activeSticker: state.activeSticker,
      currentShape: state.currentShape,
      lineStartPoint: state.lineStartPoint,
      shapeStartPoint: state.shapeStartPoint,
      currentLinePoints: state.currentLinePoints,
      setShapeStartPoint: state.setShapeStartPoint,
      setCurrentLinePoints: state.setCurrentLinePoints,
    }))
  );

  // Consolidated mouse move handler
  const handleMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();
      if (!stage) return;
      const pos = stage.getPointerPosition();
      if (!pos) return;

      if (activeTool === 'eraser' && isDrawing) {
        handleEraserLogic(stage, pos, false);
        return;
      }

      if (isDrawing && ['pen', 'ephemeralPen'].includes(activeTool)) {
        setCurrentLinePoints([...currentLinePoints, pos.x, pos.y]);
      }

      if (activeTool === 'line' && lineStartPoint) {
        handleLineMouseMove(pos);
      }

      // Handle shape preview while dragging
      if (isDrawing && shapeStartPoint && currentShape) {
        handleShapeMouseMove(pos, stage);
      }
    },
    [
      activeTool,
      isDrawing,
      lineStartPoint,
      shapeStartPoint,
      currentShape,
      handleEraserLogic,
      setCurrentLinePoints,
      currentLinePoints,
      handleLineMouseMove,
      handleShapeMouseMove,
    ]
  );

  // Mouse up handler
  const handleMouseUp = useCallback(() => {
    // Clear any text selection to prevent browser drag-and-drop issues which cause "not allowed" cursor
    if (typeof window !== 'undefined' && window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }

    if (['pen', 'ephemeralPen'].includes(activeTool) && isDrawing) {
      handlePenMouseUp();
    }

    if (activeTool === 'eraser' && isDrawing) {
      // Use a dummy stage and position since we're in mouse up
      const dummyStage = { getIntersection: () => null } as any;
      handleEraserLogic(dummyStage, { x: 0, y: 0 }, true);
      setIsDrawing(false); // Reset drawing state after eraser operation
      return;
    }

    if (activeTool === 'line' && lineStartPoint) {
      // Line tool is handled on click, not mouse up
      return;
    }

    if (isDrawing && currentShape) {
      handleShapeMouseUp();
    }

    setIsDrawing(false);
  }, [
    activeTool,
    isDrawing,
    lineStartPoint,
    currentShape,
    setIsDrawing,
    handlePenMouseUp,
    handleEraserLogic,
    handleShapeMouseUp,
  ]);

  // Mouse down handler
  const handleMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (activeTool !== 'sticky' && activeTool !== 'text') {
        e.evt?.preventDefault();
      }

      // If clicking on an existing text with text tool, let the text's onClick handle it
      if (activeTool === 'text') {
        const targetType = e.target.getType();
        const targetName = e.target.getClassName();
        // Check if we clicked on a Text shape
        if (targetType === 'Text' || targetName === 'Text') {
          // Don't create new text, let the onClick handler deal with it
          e.cancelBubble = true;
          return;
        }
      }

      const stage = e.target.getStage();
      if (!stage) return;
      const pos = stage.getPointerPosition();
      if (!pos) return;

      if (activeTool === 'eraser') {
        setIsDrawing(true);
        setCurrentLinePoints([pos.x, pos.y]);
        // Reset eraser state
        const { erasingObjectsRef, lastEraserPosRef } = getEraserRefs();
        erasingObjectsRef.current.clear();
        lastEraserPosRef.current = { x: pos.x, y: pos.y };
        return;
      }

      if (['pen', 'ephemeralPen'].includes(activeTool)) {
        setIsDrawing(true);
        setCurrentLinePoints([pos.x, pos.y]);
      }

      if (activeTool === 'line') {
        handleLineClick(pos);
      }

      // Handle shape drawing (rectangle, circle, arrow, triangle)
      if (activeTool === 'shapes' && activeShape) {
        setIsDrawing(true);
        setShapeStartPoint({ x: pos.x, y: pos.y });
        startShapeDrawing(pos);
      }

      if (activeTool === 'sticker' && activeSticker) {
        setIsDrawing(true);
        createSticker(pos, stageRef);
        setIsDrawing(false);
      }

      if (activeTool === 'text' && !isEditingText) {
        createNewText(pos, stageRef);
      }

      if (activeTool === 'sticky') {
        createStickyNote(e, stageRef);
      }
    },
    [
      activeTool,
      activeShape,
      activeSticker,
      isEditingText,
      setIsDrawing,
      setCurrentLinePoints,
      getEraserRefs,
      handleLineClick,
      setShapeStartPoint,
      startShapeDrawing,
      createSticker,
      createNewText,
      stageRef,
      createStickyNote,
    ]
  );

  return {
    handleMouseMove,
    handleMouseUp,
    handleMouseDown,
  };
};

export default useEventHandlers;
